<template>
  <div id="edit-board" class="edit-board">
    <h2 class="page-heading">Edit Board</h2>
    <form @submit.prevent="updateBoard" @keydown.enter.prevent>
      <div class="form-row-wrapper">
        <div class="form-row">
          <label for="boardId">Board ID <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="boardId" type="text" v-model="boardId" required readonly />
        </div>
        <div class="form-row">
          <label for="name">Name <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="name" type="text" @input="onNameInput" v-model="name" required />
        </div>
        <div class="form-row">
          <label for="slug">Slug <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="slug" type="text" v-model="slug" required readonly />
        </div>
        <div class="form-row">
          <label for="promptPrefix">Prompt prefix</label>
          <input id="promptPrefix" type="text" v-model="promptPrefix" />
        </div>
        <div class="form-row select">
          <label for="category">Category <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="category" v-model="category" required>
            <option v-for="category in categorySelectData" :value="category.CONSTANT" :key="category.CONSTANT">
              {{ category.DISPLAY_NAME }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row select">
          <label for="mode">Mode <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="mode" v-model="mode" required>
            <option v-for="boardMode in boardModeSelectData" :value="boardMode.value" :key="boardMode.value">
              {{ boardMode.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row">
          <label for="imagesFolderName">Images folder name <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="imagesFolderName" type="text" v-model="imagesFolderName" required />
        </div>
        <div class="form-row">
          <label for="keywordsCommaSeparated">Keywords <span class="keywords-subtext">(comma separated)</span></label>
          <input id="keywordsCommaSeparated" type="text" v-model="keywordsCommaSeparated" @input="onKeywordsInput" @blur="onKeywordsBlur" />
        </div>
        <div class="form-row select">
          <label for="boardPortraitLayout">Portrait layout <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="boardPortraitLayout" v-model="boardPortraitLayout" required>
            <option v-for="layout in boardPortraitLayoutSelectData" :value="layout.value" :key="layout.value">
              {{ layout.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row select">
          <label for="tileBackgroundSize">Tile background size <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="tileBackgroundSize" v-model="tileBackgroundSize" required>
            <option v-for="size in tileBackgroundSizesSelectData" :value="size.value" :key="size.value">
              {{ size.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row select">
          <label for="tileBackgroundPosition">Tile background position <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="tileBackgroundPosition" v-model="tileBackgroundPosition" required>
            <option v-for="position in tileBackgroundPositionsSelectData" :value="position.value" :key="position.value">
              {{ position.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row">
          <label class="control control--checkbox"
            >Tile padding
            <input type="checkbox" v-model="tilePadding" />
            <div class="control__indicator"></div>
          </label>
        </div>
        <div class="form-row">
          <label class="control control--checkbox"
            >Invert tile images in Dark Mode
            <input type="checkbox" v-model="invertTileImagesInDarkMode" />
            <div class="control__indicator"></div>
          </label>
        </div>
        <div class="form-row">
          <label for="tileBackgroundColor">Tile background colour</label>
          <div class="form-row__inner-row">
            <input id="tileBackgroundColor" type="text" v-model="tileBackgroundColor" />
            <label for="tileBackgroundColorPicker" class="color-picker__label" :style="backgroundColorPickerLabelInlineStyle"><span class="sr-only">Colour picker</span></label>
            <input id="tileBackgroundColorPicker" type="color" v-model="tileBackgroundColor" class="color-picker__input sr-only" />
          </div>
        </div>
        <div class="form-row">
          <label for="tileRadialGradientHexColor">Tile radial gradient hex colour</label>
          <div class="form-row__inner-row">
            <input id="tileRadialGradientHexColor" type="text" v-model="tileRadialGradientHexColor" />
            <label for="tileRadialGradientHexColorPicker" class="color-picker__label" :style="radialGradientColorPickerLabelInlineStyle"
              ><span class="sr-only">Colour picker</span></label
            >
            <input id="tileRadialGradientHexColorPicker" type="color" v-model="tileRadialGradientHexColor" class="color-picker__input sr-only" />
          </div>
        </div>
        <div class="form-row form-row--last">
          <label class="control control--checkbox"
            >Promoted
            <input type="checkbox" v-model="promoted" />
            <div class="control__indicator"></div>
          </label>
        </div>
      </div>
      <p v-if="slugHasAlreadyBeenUsed" class="warning-message">This slug has been used before, please change the name so the slug is unique.</p>
      <p v-if="slugIsTheSameAsId" class="warning-message">The name/slug cannot be the same as the Board ID.</p>
      <p v-if="slugIsTheSameAsExistingBoardId" class="warning-message">The name/slug cannot be the same as an existing Board ID.</p>
      <button type="submit">Save edits</button>
      <router-link
        v-if="boardId"
        :to="{
          name: 'view-board',
          params: {boardId: boardId},
        }"
        class="cancel-link"
        >Cancel</router-link
      >
      <button type="button" class="delete-board-btn" @click="onDeleteBoardClick">Delete this board</button>
    </form>
  </div>
</template>

<script>
import kebabCase from 'lodash/kebabCase';
import db from './../firebaseInit';
import sortBy from 'lodash/sortBy';
import router from './../router/index';
import hexToHsl from './../helpers/hexToHsl';
import arrayToCsv from './../helpers/arrayToCsv';
import csvToArray from './../helpers/csvToArray';
import getBoardsDataFromFirebase from './../helpers/getBoardsDataFromFirebase';
import {
  BOARD_MODES_SELECT_DATA,
  BOARD_MODES,
  BOARD_PORTRAIT_LAYOUTS_SELECT_DATA,
  BOARD_PORTRAIT_LAYOUTS,
  CATEGORIES,
  DEFAULT_BOARD_MODE,
  DEFAULT_BOARD_PORTRAIT_LAYOUT,
  DEFAULT_PROMOTED,
  DEFAULT_TILE_BACKGROUND_COLOR,
  DEFAULT_TILE_BACKGROUND_POSITION,
  DEFAULT_TILE_BACKGROUND_SIZE,
  IMAGES_FOLDER_NAME_DELIMITER,
  NOTIFICATION_SETTINGS,
  TILE_BACKGROUND_POSITIONS_SELECT_DATA,
  TILE_BACKGROUND_POSITIONS,
  TILE_BACKGROUND_SIZES_SELECT_DATA,
  TILE_BACKGROUND_SIZES,
} from './../constants/globalConstants';

export default {
  name: 'edit-board',
  title: 'Edit Board',
  data() {
    return {
      boardCreatedAt: {},
      boardId: '',
      boardPortraitLayout: '',
      category: '',
      existingBoardIds: [],
      existingSlugs: [],
      imagesFolderName: '',
      invertTileImagesInDarkMode: false,
      keywords: [],
      keywordsCommaSeparated: '',
      mode: '',
      name: '',
      originalName: '',
      originalSlug: '',
      promoted: false,
      promptPrefix: '',
      slug: '',
      slugHasAlreadyBeenUsed: false,
      slugIsTheSameAsId: false,
      slugIsTheSameAsExistingBoardId: false,
      submittingForm: false,
      tileBackgroundPosition: '',
      tileBackgroundSize: '',
      tileBackgroundColor: '',
      tilePadding: false,
      tileRadialGradientHexColor: '',
    };
  },
  computed: {
    addImagesFolderNameActiveClass: function() {
      return {
        'force-active': this.imagesFolderName !== null && this.imagesFolderName !== '',
      };
    },
    boardModeSelectData: function() {
      return BOARD_MODES_SELECT_DATA;
    },
    boardPortraitLayoutSelectData: function() {
      return BOARD_PORTRAIT_LAYOUTS_SELECT_DATA;
    },
    categorySelectData: function() {
      return sortBy(CATEGORIES, 'DISPLAY_NAME');
    },
    tileBackgroundPositionsSelectData: function() {
      return TILE_BACKGROUND_POSITIONS_SELECT_DATA;
    },
    tileBackgroundSizesSelectData: function() {
      return TILE_BACKGROUND_SIZES_SELECT_DATA;
    },
    backgroundColorPickerLabelInlineStyle: function() {
      if (this.tileBackgroundColor) {
        return `background-color: ${this.tileBackgroundColor}; border: 0;`;
      } else {
        return '';
      }
    },
    radialGradientColorPickerLabelInlineStyle: function() {
      if (this.tileRadialGradientHexColor) {
        return `background-color: ${this.tileRadialGradientHexColor}; border: 0;`;
      } else {
        return '';
      }
    },
    tileBackgroundColorAsHsl: function() {
      return this.tileBackgroundColor ? hexToHsl(this.tileBackgroundColor) : '';
    },
  },
  beforeRouteEnter(to, from, next) {
    db.collection('boards')
      .where('boardId', '==', to.params.boardId)
      .get()
      .then(snapshot => {
        if (snapshot.size >= 1) {
          snapshot.forEach(doc => {
            next(vm => {
              vm.boardCreatedAt = doc.data().boardCreatedAt;
              vm.boardId = doc.data().boardId;
              vm.boardPortraitLayout = doc.data().boardPortraitLayout;
              vm.category = doc.data().category;
              vm.imagesFolderName = doc.data().imagesFolderName;
              vm.invertTileImagesInDarkMode = doc.data().invertTileImagesInDarkMode;
              vm.keywords = doc.data().keywords;
              vm.keywordsCommaSeparated = arrayToCsv(doc.data().keywords);
              vm.mode = doc.data().mode;
              vm.name = doc.data().name;
              vm.promoted = doc.data().promoted;
              vm.promptPrefix = doc.data().promptPrefix;
              vm.slug = doc.data().slug;
              vm.originalName = doc.data().name;
              vm.originalSlug = doc.data().slug;
              vm.tileBackgroundPosition = doc.data().tileBackgroundPosition;
              vm.tileBackgroundSize = doc.data().tileBackgroundSize;
              vm.tileBackgroundColor = doc.data().tileBackgroundColor;
              vm.tilePadding = doc.data().tilePadding;
              vm.tileRadialGradientHexColor = doc.data().tileRadialGradientHexColor;
            });
          });
        } else {
          router.replace({name: 'boards'});
        }
      });
  },
  watch: {
    // $route: 'fetchData',
  },
  created() {
    window.scrollTo(0, 0);
    db.collection('boards')
      .orderBy('boardId')
      .get()
      .then(snapshot => {
        snapshot.forEach(doc => {
          this.$data.existingBoardIds.push(doc.data().boardId);
          this.$data.existingSlugs.push(doc.data().slug);
        });
        this.$data.existingSlugs = this.$data.existingSlugs.filter(slug => slug !== '');
      });
  },
  methods: {
    fetchData() {
      // db.collection('employees')
      //   .where('employee_id', '==', this.$route.params.employee_id)
      //   .get()
      //   .then(snapshot => {
      //     snapshot.forEach(doc => {
      //       this.boardCreatedAt = doc.data.boardCreatedAt;
      //       this.boardId = doc.data.boardId;
      //       this.boardPortraitLayout = doc.data.boardPortraitLayout;
      //       this.category = doc.data.category;
      //       this.imagesFolderName = doc.data.imagesFolderName;
      //       this.mode = doc.data.mode;
      //       this.name = doc.data.name;
      //       this.promoted = doc.data.promoted;
      //       this.tileBackgroundSize = doc.data.tileBackgroundSize;
      //       this.tileBackgroundColor = doc.data.tileBackgroundColor;
      //     });
      //   });
    },
    onNameInput() {
      this.slug = kebabCase(this.name);
    },
    onKeywordsInput() {
      this.keywordsCommaSeparated = this.keywordsCommaSeparated.toLowerCase();
    },
    onKeywordsBlur() {
      this.keywords = csvToArray(this.keywordsCommaSeparated);
      this.keywordsCommaSeparated = arrayToCsv(this.keywords);
    },
    updateBoard() {
      if (this.submittingForm) return;

      let isError = false;

      if (this.slug !== this.originalSlug && this.existingSlugs.includes(this.slug)) {
        this.slugHasAlreadyBeenUsed = true;
        isError = true;
      } else {
        this.slugHasAlreadyBeenUsed = false;
      }

      if (this.slug.trim() === this.boardId.trim()) {
        this.slugIsTheSameAsId = true;
        isError = true;
      } else {
        this.slugIsTheSameAsId = false;
        if (this.existingBoardIds.includes(this.slug)) {
          this.slugIsTheSameAsExistingBoardId = true;
          isError = true;
        } else {
          this.slugIsTheSameAsExistingBoardId = false;
        }
      }

      if (!isError) {
        this.submittingForm = true;
        db.collection('boards')
          .where('boardId', '==', this.$route.params.boardId)
          .get()
          .then(snapshot => {
            snapshot.forEach(doc => {
              doc.ref
                .update({
                  category: this.category,
                  boardPortraitLayout: this.boardPortraitLayout,
                  imagesFolderName: this.imagesFolderName,
                  invertTileImagesInDarkMode: this.invertTileImagesInDarkMode || false,
                  keywords: this.keywords,
                  mode: this.mode,
                  name: this.name,
                  promoted: this.promoted,
                  promptPrefix: this.promptPrefix,
                  slug: this.slug,
                  tileBackgroundPosition: this.tileBackgroundPosition,
                  tileBackgroundSize: this.tileBackgroundSize,
                  tileBackgroundColor: this.tileBackgroundColor,
                  tilePadding: this.tilePadding,
                  tileRadialGradientHexColor: this.tileRadialGradientHexColor,
                })
                .then(() => {
                  getBoardsDataFromFirebase(boardsData => {
                    this.$store.dispatch('boardsVuexModule/SET_BOARDS_DATA_FROM_FIREBASE_ACTION', boardsData);
                    this.$snotify.success('Board updated', '', NOTIFICATION_SETTINGS).on('destroyed', () => {
                      this.submittingForm = false;
                      this.$router.push({name: 'view-board', params: {boardId: this.boardId}});
                    });
                  });
                })
                .catch(err => {
                  this.$snotify.error('Error updating the board', '', NOTIFICATION_SETTINGS);
                });
            });
          });
      }
    },
    onDeleteBoardClick() {
      this.$confirm({
        message: `Are you sure you want to delete this board?`,
        button: {
          no: 'No',
          yes: 'Yes',
        },
        callback: confirm => {
          if (confirm) {
            // TODO
            alert('DELETE THE BOARD FROM FIREBASE TO GO HERE!');
          }
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.input-field--checkbox {
  min-height: 3rem;
}

.form-row__inner-row {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.color-picker__label {
  align-items: center;
  border: 1px solid var(--color-picker-label-border);
  border-radius: var(--border-radius);
  cursor: pointer;
  display: flex;
  flex-basis: 0;
  justify-content: center;
  margin-bottom: 0;
  margin-left: 1rem;
  min-width: 5rem;
  width: 5rem;
}

.cancel-link {
  margin-top: 2rem;
}

.keywords-subtext {
  color: var(--text-color-alpha-50);
}

.delete-board-btn {
  background-color: red;
  margin: 14rem auto 0 auto;
}
</style>
